import { Injectable } from '@angular/core';
import { DataContextService, ResultWrapper } from '@seahorse/common';
import type { Guid } from 'guid-typescript';
import { Observable, Subject } from 'rxjs';
import { AdminUserModel, UserPermission } from '../models/users.model';

@Injectable({
  providedIn: 'root',
})
export class AdminUsersDataService {
  private tableSource = new Subject<void>();

  constructor(private dataContext: DataContextService) {}

  tableSourceChanged$ = this.tableSource.asObservable();

  search(
    searchQuery?: string,
    pageIndex?: number,
    pageSize?: number,
    sortfield = 'CreatedOn',
    orderAscending = false,
    allUsers = false
  ): Observable<ResultWrapper<AdminUserModel[]>> {
    const queryParams = [
      `sortfield=${sortfield}`,
      `allUsers=${allUsers}`,
      `orderascending=${orderAscending}`,
    ];

    if (searchQuery !== undefined && searchQuery !== null) {
      queryParams.push(`searchTerm=${encodeURIComponent(searchQuery)}`);
    }

    if (pageIndex !== undefined && pageIndex !== null) {
      queryParams.push(`pindex=${pageIndex}`);
    }

    if (pageSize !== undefined && pageSize !== null) {
      queryParams.push(`psize=${pageSize}`);
    }

    const queryString = queryParams.join('&');
    const url = `user/management/search?${queryString}`;

    return this.dataContext.get<AdminUserModel[]>(url);
  }

  add(model: AdminUserModel): Observable<ResultWrapper<AdminUserModel>> {
    return this.dataContext.post<AdminUserModel>(`user/management`, model);
  }

  update(model: AdminUserModel): Observable<ResultWrapper<AdminUserModel>> {
    let url = `user/management/${model.id}`;

    if (model.sendEmail) {
      url += '?sendEmail=true';
    }

    return this.dataContext.put<AdminUserModel>(url, model);
  }

  setNewPassword(
    userId: string,
    newPassword: string,
    sendEmail: boolean
  ): Observable<ResultWrapper<boolean>> {
    let url = `user/management/${userId}/set-new-password`;

    if (sendEmail) {
      url += '?sendEmail=true';
    }

    return this.dataContext.put<boolean>(url, { value: newPassword });
  }

  deleteUser(id: string): Observable<ResultWrapper<AdminUserModel>> {
    return this.dataContext.delete<AdminUserModel>(`user/management/${id}`);
  }

  getUserById(id: string): Observable<ResultWrapper<AdminUserModel>> {
    return this.dataContext.get<AdminUserModel>(`user/management/${id}`);
  }

  getUsersWithPermissons(): Observable<ResultWrapper<UserPermission[]>> {
    return this.dataContext.get<UserPermission[]>(`user/management/report`);
  }

  getUsersByOrganisationId(
    pageIndex: number,
    pageSize: number,
    organisationId: Guid
  ): Observable<ResultWrapper<AdminUserModel[]>> {
    return this.dataContext.get<AdminUserModel[]>(
      `user/management/byorganisation?organisationId=${organisationId}&pindex=${pageIndex}&psize=${pageSize}`
    );
  }

  updateTable() {
    this.tableSource.next();
  }

  sendWelcomeEmails(
    userId: string,
    organisationId: string,
    username: string,
    emails: string[]
  ): Observable<ResultWrapper<boolean>> {
    return this.dataContext.post<boolean>(
      `user/management/notification/welcome-email`,
      {
        userId,
        organisationId,
        username,
        emails,
      }
    );
  }

  removeMfa(userId: string): Observable<ResultWrapper<AdminUserModel>> {
    return this.dataContext.get<AdminUserModel>(
      `user/management/mfa/remove?userId=${userId}`
    );
  }
}
